import type { PrintGatewayStatus } from "../../gatewayStatus";
import { GatewayStatusCard } from "./GatewayStatusCard";
import { t, translateMessage } from "../../i18n";
import { useState } from "react";
import { ArrowDown, ArrowUp, Check, Pencil, Plus, Printer, Trash2, Wifi, X } from "lucide-react";
import type { PrinterConfig, Setup } from "../../types";
import { useBackHandler } from "../../backNavigation";

type Props = {
  back: React.ReactNode;
  setup: Setup;
  onLoadGatewayStatus: () => Promise<PrintGatewayStatus | null>;
  onSaveSetup: (next: Setup) => void;
};

const emptyPrinter = (id: number, sortOrder: number): PrinterConfig => ({
  id,
  device_name: "",
  nickname: "",
  host: "",
  port: 9100,
  enabled: 1,
  sort_order: sortOrder
});

export function ServerPrintersPage({ back, setup, onSaveSetup, onLoadGatewayStatus }: Props) {
  const [editing, setEditing] = useState<PrinterConfig | null>(null);
  const [confirmRemove, setConfirmRemove] = useState<PrinterConfig | null>(null);
  useBackHandler(Boolean(editing), () => setEditing(null));
  useBackHandler(Boolean(confirmRemove), () => setConfirmRemove(null));
  const [note, setNote] = useState("");

  const printers = setup.printers.slice().sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);

  const commit = (next: PrinterConfig[]): void => {
    onSaveSetup({ ...setup, printers: next });
  };

  const savePrinter = (): void => {
    if (!editing || !editing.host.trim() || editing.port < 1 || editing.port > 65535) {
      setNote(t("Συμπληρώστε έγκυρη IP και port."));
      return;
    }
    const exists = printers.some((printer) => printer.id === editing.id);
    commit(
      exists
        ? printers.map((printer) => (printer.id === editing.id ? editing : printer))
        : [...printers, editing]
    );
    setEditing(null);
    setNote("");
  };

  const addPrinter = (): void => {
    const id = printers.reduce((highest, printer) => Math.max(highest, printer.id), 0) + 1;
    setEditing(emptyPrinter(id, printers.length));
  };

  const movePrinter = (id: number, offset: number): void => {
    const index = printers.findIndex((printer) => printer.id === id);
    const target = Math.max(0, Math.min(printers.length - 1, index + offset));
    if (index < 0 || index === target) return;
    const next = printers.slice();
    const [moving] = next.splice(index, 1);
    next.splice(target, 0, moving);
    commit(next.map((printer, sort_order) => ({ ...printer, sort_order })));
  };

  return (
    <section className="screen settings-screen" aria-labelledby="settings-heading">
      {back}
      <div className="screen-heading-row">
        <div>
          <h1 id="settings-heading">{t("Server / Εκτυπωτές")}</h1>
          <p>{t("Ξεχωριστή σύνδεση και όνομα για κάθε εκτυπωτή")}</p>
        </div>
      </div>

      <div className="settings-groups">
        <GatewayStatusCard onLoad={onLoadGatewayStatus} />
        <section className="settings-group">
          <p className="hint-text">{t("Οι παραγγελίες φτάνουν στους εκτυπωτές μέσω της ρυθμισμένης πύλης του καταστήματος ή μιας συσκευής Android στο ίδιο δίκτυο. Κρατήστε σταθερή τη διεύθυνση κάθε εκτυπωτή με DHCP reservation στο router ή με στατική IP εκτός του DHCP range.")}</p>
          <button type="button" className="primary-command wide" onClick={addPrinter}>
            <Plus />
            <span>{t("Προσθήκη εκτυπωτή")}</span>
          </button>
        </section>

        <section className="settings-group">
          {printers.length ? (
            printers.map((printer, index) => (
              <div className="status-row" key={printer.id}>
                <Printer />
                <span>
                  <strong>{printer.nickname || printer.device_name || printer.host}</strong>
                  <small>
                    {printer.host}:{printer.port} • {printer.enabled ? t("ενεργός") : t("ανενεργός")}
                  </small>
                </span>
                <span className="admin-row-actions">
                  <button type="button" className="icon-button" disabled={index === 0} onClick={() => movePrinter(printer.id, -1)} aria-label={t("Πάνω")}><ArrowUp /></button>
                  <button type="button" className="icon-button" disabled={index === printers.length - 1} onClick={() => movePrinter(printer.id, 1)} aria-label={t("Κάτω")}><ArrowDown /></button>
                  <button type="button" className="icon-button" onClick={() => setEditing(printer)} aria-label={t("Ρύθμιση")} title={t("Ρύθμιση")}>
                    <Pencil />
                  </button>
                  <button
                    type="button"
                    className="icon-button"
                    onClick={() => setConfirmRemove(printer)}
                    aria-label={t("Διαγραφή {0}", printer.nickname || printer.host)}
                  >
                    <Trash2 />
                  </button>
                </span>
              </div>
            ))
          ) : (
            <p className="hint-text">{t("Δεν υπάρχουν ρυθμισμένοι εκτυπωτές.")}</p>
          )}
        </section>
      </div>

      {editing ? (
        <div className="dialog-backdrop">
          <section className="details-dialog" role="dialog" aria-modal="true" aria-label={t("Ρύθμιση εκτυπωτή")}>
            <header className="dialog-header">
              <div>
                <h2>{t("Ρύθμιση εκτυπωτή")}</h2>
                <p>{t("Διεύθυνση δικτύου, μοντέλο και nickname")}</p>
              </div>
              <button type="button" className="icon-button" onClick={() => setEditing(null)} aria-label={t("Κλείσιμο")}><X /></button>
            </header>
            <div className="dialog-scroll">
              <label className="field">
                <span>{t("Όνομα (nickname)")}</span>
                <input
                  value={editing.nickname}
                  onChange={(event) => setEditing({ ...editing, nickname: event.target.value })}
                />
              </label>
              <label className="field">
                <span>{t("Όνομα συσκευής / μοντέλο")}</span>
                <input
                  value={editing.device_name}
                  placeholder={t("π.χ. Imin D4-504")}
                  onChange={(event) => setEditing({ ...editing, device_name: event.target.value })}
                />
              </label>
              <label className="field">
                <span>{t("IP εκτυπωτή")}</span>
                <input
                  value={editing.host}
                  inputMode="decimal"
                  autoFocus
                  onChange={(event) => setEditing({ ...editing, host: event.target.value })}
                />
              </label>
              <label className="field">
                <span>Port</span>
                <input
                  value={String(editing.port)}
                  inputMode="numeric"
                  onChange={(event) =>
                    setEditing({ ...editing, port: Number(event.target.value.replace(/\D/g, "")) || 0 })
                  }
                />
              </label>
              <div className="option-group check-grid">
                <label className="check-option">
                  <input
                    type="checkbox"
                    checked={editing.enabled === 1}
                    onChange={() => setEditing({ ...editing, enabled: editing.enabled === 1 ? 0 : 1 })}
                  />
                  <span>{t("Ενεργός")}</span>
                </label>
              </div>
              <div className="network-note"><Wifi /><span>{t("Η πύλη ή η συσκευή Android που εκτυπώνει πρέπει να μπορεί να φτάσει αυτή την IP στο δίκτυο του καταστήματος.")}</span></div>
              {note ? <p className="hint-text danger-text">{translateMessage(note)}</p> : null}
            </div>
            <footer className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setEditing(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                disabled={!editing.host.trim()}
                onClick={savePrinter}
              >
                <Check />
                <span>{t("Αποθήκευση")}</span>
              </button>
            </footer>
          </section>
        </div>
      ) : null}

      {confirmRemove ? (
        <div className="dialog-backdrop">
          <section className="permission-dialog" role="dialog" aria-modal="true" aria-label={t("Διαγραφή εκτυπωτή")}>
            <header>
              <h2>{t("Διαγραφή εκτυπωτή")}</h2>
              <p>{t("Να διαγραφεί ο \"")}{confirmRemove.nickname || confirmRemove.host}";</p>
            </header>
            <div className="dialog-actions">
              <button type="button" className="secondary-command" onClick={() => setConfirmRemove(null)}>
                <span>{t("Άκυρο")}</span>
              </button>
              <button
                type="button"
                className="primary-command"
                onClick={() => {
                  commit(printers.filter((printer) => printer.id !== confirmRemove.id));
                  setConfirmRemove(null);
                }}
              >
                <span>{t("Διαγραφή")}</span>
              </button>
            </div>
          </section>
        </div>
      ) : null}
    </section>
  );
}
